import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import {
  CombineSurveyTrafficReportParams,
  ProjectManager,
  SurveyTransactionStatus,
  TemplateFolder,
} from '@ps/survey-utils';
import { ProjectManagerMapper } from './mappers/project-manager.mapper';
import { SurveyTemplateMapper } from './mappers/survey-template.mapper';
import {
  OperatorProjectManagersResponseDto,
  ProjectManagerDto,
} from '../dtos/project-manager.dto';
import { TemplateFolderDto } from '../dtos/template-folder.dto';
import { CombineSurveyTrafficReportDto } from '../dtos/reports.dto';
import { CombinedSTPayloadMapper } from './mappers/combined-st-report.mapper';

export interface GetTemplatesParams {
  hasImpersonatedBuyer: boolean;
  companyId: number;
}

@Injectable({ providedIn: 'root' })
export class SurveyReferenceApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = this._env.pureSpectrum.url;
  private readonly _projectManagersMapper = new ProjectManagerMapper();
  private readonly _templateMapper = new SurveyTemplateMapper();
  private readonly _reportPayloadMapper = new CombinedSTPayloadMapper();

  public getBuyerManagers(companyId: number): Observable<ProjectManager[]> {
    return this._http
      .get<ProjectManagerDto[]>(`${this._baseUrl}/companies/${companyId}/users`)
      .pipe(map((dtos) => this._projectManagersMapper.fromDtoList(dtos)));
  }

  public getOperatorManagers(): Observable<ProjectManager[]> {
    return this._http
      .get<OperatorProjectManagersResponseDto>(
        `${this._baseUrl}/operator-users`,
      )
      .pipe(map(({ users }) => this._projectManagersMapper.fromDtoList(users)));
  }

  public getTemplates({
    hasImpersonatedBuyer,
    companyId,
  }: GetTemplatesParams): Observable<TemplateFolder[]> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        is_service_buyer: hasImpersonatedBuyer,
        parent_company_id: companyId,
      },
    });
    return this._http
      .get<
        TemplateFolderDto[]
      >(`${this._baseUrl}/buyers/v2/templates`, { params })
      .pipe(map((dtos) => this._templateMapper.fromDtoList(dtos)));
  }

  public getTransactionStatuses(): Observable<SurveyTransactionStatus[]> {
    return this._http.get<SurveyTransactionStatus[]>(
      `${this._baseUrl}/survey-transaction-status`,
    );
  }

  public getCombinedSurveyTrafficReport(
    data: CombineSurveyTrafficReportParams,
  ): Observable<CombineSurveyTrafficReportDto> {
    const payload = this._reportPayloadMapper.toPayload(data);
    return this._http.get<CombineSurveyTrafficReportDto>(
      `${this._baseUrl}/reports/surveys/traffic-report`,
      { params: new HttpParams({ fromObject: { ...payload } }) },
    );
  }

  public downloadCSTReport(filename: string): Observable<Blob> {
    return this._http.get(
      `${this._baseUrl}/reports/download-report/${filename}`,
      { responseType: 'blob' },
    );
  }
}
